import { useState, useEffect, useRef } from "react";
import type { SiteVersion } from "@/types/sites";
import {
  assembleSiteVersion,
  fetchManifestBySiteId,
  type ViewerManifest,
} from "@/utils/viewerManifest";
import { applyTradeAccentCssVars } from "@/utils/paletteUtils";

// Cache TTL: 5 minutes in dev, 10 minutes in production
const TTL_MS = (import.meta.env.PROD ? 10 : 5) * 60 * 1000;

interface UsePublishedVersionOptions {
  /** Optional manifest to hydrate the hook without extra network calls */
  manifest?: ViewerManifest | null;
  /** When true, skip remote fetches once manifest data is available */
  skipRemoteFetch?: boolean;
}

export const usePublishedVersion = (
  siteId: string | undefined,
  language?: string,
  options?: UsePublishedVersionOptions
) => {
  const [publishedVersion, setPublishedVersion] = useState<SiteVersion | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef<{ id?: string; updatedAt?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPublishedVersion = async () => {
      if (!siteId) {
        setError("No site siteId provided");
        setIsLoading(false);
        return;
      }

      const requestedLang = language;
      const cacheKey = `viewerManifest:site:${siteId}`;
      const manifestOverride = options?.manifest;
      const shouldUseOverride =
        !!manifestOverride && manifestOverride.siteId === siteId;

      const persistManifest = (manifest: ViewerManifest) => {
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ data: manifest, cachedAt: Date.now() })
          );
        } catch {
          /* ignore cache write errors */
        }
      };

      const buildVersionFromManifest = async (
        manifest: ViewerManifest,
        loadPalette: boolean
      ) => {
        const { version } = assembleSiteVersion(manifest, requestedLang);
        if (loadPalette && version?.config?.theme?.paletteName) {
          try {
            const { loadPalette: loadPaletteFn } = await import("@/utils/palettes");
            const palette = await loadPaletteFn(version.config.theme.paletteName);
            if (palette) {
              version.config = {
                ...version.config,
                theme: {
                  ...version.config.theme,
                  classes: {
                    ...version.config.theme?.classes,
                    ...palette.classes,
                  },
                },
              } as typeof version.config;
              // Sync Trade accent CSS variables with palette accent color
              

              applyTradeAccentCssVars(version.config.theme);
            }
          } catch (paletteError) {
            console.warn(
              `Failed to load palette "${version.config?.theme?.paletteName}":`,
              paletteError
            );
          }
        }
        return version;
      };

      const applyManifest = async (
        manifest: ViewerManifest,
        {
          loadPalette = false,
          cache = true,
        }: { loadPalette?: boolean; cache?: boolean } = {}
      ) => {
        if (!manifest || cancelled) return false;

        if (manifest.suspension?.active) {
          const message =
            manifest.suspension.message || "This site is currently suspended";
          if (!cancelled) {
            setPublishedVersion(null);
            setError(message);
            setIsLoading(false);
          }
          if (cache) {
            persistManifest(manifest);
          }
          return false;
        }

        const version = await buildVersionFromManifest(manifest, loadPalette);
        if (cancelled) return false;

        setError(null);
        setPublishedVersion(version);
        versionRef.current = {
          id: manifest.versionId,
          updatedAt: manifest.publishedAt,
        };

        if (cache) {
          persistManifest(manifest);
        }
        return true;
      };

      let hasData = false;

      if (shouldUseOverride && manifestOverride) {
        const overrideApplied = await applyManifest(manifestOverride, {
          loadPalette: true,
        });
        setIsLoading(false);
        if (!overrideApplied) {
          return;
        }
        hasData = true;
        if (options?.skipRemoteFetch) {
          return;
        }
      }

      if (!hasData) {
        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const raw = JSON.parse(cached) as any;
            const wrapper =
              raw && typeof raw === "object" && "data" in raw && "cachedAt" in raw
                ? raw
                : { data: raw, cachedAt: 0 };

            const manifest = wrapper.data as ViewerManifest | null;
            const cachedAt =
              typeof wrapper.cachedAt === "number" ? wrapper.cachedAt : 0;
            const isFresh = cachedAt > 0 && Date.now() - cachedAt < TTL_MS;

            if (manifest) {
              const cacheApplied = await applyManifest(manifest, {
                cache: false,
              });
              if (!cacheApplied) {
                return;
              }
              hasData = true;
              if (isFresh) {
                return;
              }
            }
          }
        } catch {
          /* ignore cache errors */
        }
      }

      if (options?.skipRemoteFetch && hasData) {
        setIsLoading(false);
        return;
      }

      if (!hasData) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const manifest = await fetchManifestBySiteId(siteId);
        const applied = await applyManifest(manifest, { loadPalette: true });
        if (!applied) {
          return;
        }
      } catch (storageError) {
        console.warn("[viewer] manifest fetch failed:", storageError);
        if (!hasData) {
          const message =
            storageError instanceof Error && storageError.message
              ? storageError.message
              : "Failed to load site";
          if (
            typeof message === "string" &&
            message.toLowerCase().includes("suspend")
          ) {
            setPublishedVersion(null);
          }
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublishedVersion();

    return () => {
      cancelled = true;
    };
  }, [
    siteId,
    language,
    options?.manifest?.siteId,
    options?.manifest?.versionId,
    options?.skipRemoteFetch,
  ]);

  return {
    publishedVersion,
    isLoading,
    error,
  };
};
